import React from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Tooltip } from '@mui/material';

import { type MarketingRouterTerm, getMarketingRouterTerm } from './marketingRouterGlossary';

type Props = {
  term: MarketingRouterTerm;
  /** The visible text. Defaults to the glossary label for the term. */
  children?: React.ReactNode;
};

/**
 * A heading, column heading or field label with a hover explanation.
 *
 * The explanation sits behind a small help icon rather than on the text
 * itself, so a label still reads as a label and the icon signals that there
 * is more to know. The icon is focusable, so the explanation is reachable by
 * keyboard and announced by screen readers, not hover only.
 */
export default function MarketingRouterTermLabel({ term, children }: Props) {
  const entry = getMarketingRouterTerm(term);

  return (
    <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}>
      {children ?? entry.label}
      {/* describeChild: the tooltip is the icon's description, and the icon
          keeps a fixed name that does not repeat the label text — a label that
          contained its own term twice would confuse label lookups. */}
      <Tooltip title={entry.description} arrow describeChild>
        <Box
          component="span"
          tabIndex={0}
          role="img"
          aria-label="More information"
          sx={{
            color: 'text.secondary',
            cursor: 'help',
            display: 'inline-flex',
            lineHeight: 0,
            '&:focus-visible': { color: 'primary.main', outline: 'none' },
            '&:hover': { color: 'primary.main' },
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: '1em' }} />
        </Box>
      </Tooltip>
    </Box>
  );
}
